// @flow

import OptionManager from "./OptionManager";
import type {OptionList} from "./OptionManager";
import DefaultValueManager from "./DefaultValueManager";

export type DependencyContext = {
    diaByTubeType: number[],
    existingTube1TubeType: number,
    existingTube2TubeType: number,
    existingTube3TubeType: number,
    tcTube1TubeType: number,
    tcTube2TubeType: number,
    tcTube3TubeType: number,
    ddTube1TubeType: number,
    ddTube2TubeType: number,
    ddTube3TubeType: number,
    numExistingTubes: number,
    numTraditionalTubes: number,
    techniquesToCompare: number,
};

export default class DependencyManager {

    optionManager: OptionManager;
    defaultValueManager: DefaultValueManager;

    constructor(optionManager: OptionManager, defaultValueManager: DefaultValueManager) {
        this.optionManager = optionManager;
        this.defaultValueManager = defaultValueManager;
    }

    handleDependencies(name: string, value: number, context: DependencyContext) {
        
        let newState = {[name]: value};

        // Reset coating when type of landscape is changed, different options
        if(name === 'typeOfLandscapeBefore') {
            newState = {...newState,  ...(this.handleDependencies('coatingBefore', this.defaultValueManager.getDefaultCoating(value), context))};
        }
        if(name === 'typeOfLandscapeAfter') {
            newState = {...newState,  ...(this.handleDependencies('coatingAfter', this.defaultValueManager.getDefaultCoating(value), context))};
        }

        // Street type visibility depending on coating
        if(name === 'coatingBefore') {
            newState = {...newState,  ...(this.handleDependencies('streetTypeBefore', this.defaultValueManager.getDefaultStreetType(value), context))};
        }
        if(name === 'coatingAfter') {
            newState = {...newState,  ...(this.handleDependencies('streetTypeAfter', this.defaultValueManager.getDefaultStreetType(value), context))};
        }

        // Asphalt type visibility depending on coating
        if(name === 'coatingAfter') {
            newState = {...newState,  ...(this.handleDependencies('asphaltTypeAfter', this.defaultValueManager.getDefaultAsphaltType(value), context))};
        }
        
        // Point shaft depencencies
        newState = {...newState,  ...(this.handlePointShaftDependencies(name, value, 'ifl', newState, context))};
        newState = {...newState,  ...(this.handlePointShaftDependencies(name, value, 'tc', newState, context))};
        newState = {...newState,  ...(this.handlePointShaftDependencies(name, value, 'dd', newState, context))};
        newState = {...newState,  ...(this.handlePointShaftDependencies(name, value, 'sl', newState, context))};
        newState = {...newState,  ...(this.handlePointShaftDependencies(name, value, 'pul', newState, context))};
        
        // Set default lengths for all other techs when set for first existing tube
        if(name === 'existingTube1TubeLength') {
            newState = {...newState,  ...(this.handleDependencies('existingTube2TubeLength', value, context))};
            newState = {...newState,  ...(this.handleDependencies('existingTube3TubeLength', value, context))};
            newState = {...newState,  ...(this.handleDependencies('shaftLengthM', value, context))};
            newState = {...newState,  ...(this.handleDependencies('iflShaftLengthM', value, context))};
            newState = {...newState,  ...(this.handleDependencies('tcShaftLengthM', value, context))};
            newState = {...newState,  ...(this.handleDependencies('ddShaftLengthM', value, context))};
            newState = {...newState,  ...(this.handleDependencies('slShaftLengthM', value, context))};
            newState = {...newState,  ...(this.handleDependencies('pulShaftLengthM', value, context))};
            newState = this.setTubeLengths('traditionalTube', 3, value, newState, context);
            newState = this.setTubeLengths('iflTube', 2, value, newState, context);
            newState = this.setTubeLengths('tcTube', 3, value, newState, context);
            newState = this.setTubeLengths('ddTube', 3, value, newState, context);
            newState = this.setTubeLengths('slTube', 1, value, newState, context);
            newState = this.setTubeLengths('pulTube', 1, value, newState, context);
        }
        
        // Mirror terrain data before to after
        if(name === 'typeOfLandscapeBefore') {
            newState = {...newState,  ...(this.handleDependencies('typeOfLandscapeAfter', value, context))};
        }
        if(name === 'coatingBefore') {
            newState = {...newState,  ...(this.handleDependencies('coatingAfter', value, context))};
        }
        if(name === 'streetTypeBefore') {
            newState = {...newState,  ...(this.handleDependencies('streetTypeAfter', value, context))};
        }
        
        // Only enter one asphalt distance (not clean and polluted, but data needed in both)
        if(name === 'distanceReceiverCleanAsphaltKm') {
            newState = {...newState,  ...(this.handleDependencies('distanceReceiverPollutedAsphaltKm', value, context))};
        }
        
        // Tubes
        // Mirror traditional tubes from existing tubes
        if(name === 'numExistingTubes' && value > 0) {
            newState = {...newState,  ...(this.handleDependencies('numTraditionalTubes', value, context))};
        }
        if(name === 'existingTube1TubeType') {
            newState = {...newState,  ...(this.handleDependencies('traditionalTube1TubeType', value, context))};
        }
        if(name === 'existingTube2TubeType') {
            newState = {...newState,  ...(this.handleDependencies('traditionalTube2TubeType', value, context))};
        }
        if(name === 'existingTube3TubeType') {
            newState = {...newState,  ...(this.handleDependencies('traditionalTube3TubeType', value, context))};
        }
        
        // Set at least 1 existing/traditional tube when directed drilling is not used 
        if(name === 'techniquesToCompare' && !(value === this.optionManager.ids.TECH_DD) && context.numExistingTubes === 0) {
            newState = {...newState,  ...(this.handleDependencies('numExistingTubes', 1, context))};
        }

        if(name === 'techniquesToCompare' && (value === this.optionManager.ids.TECH_DD)) {
            newState = {...newState,  ...(this.handleDependencies('numExistingTubes', 0, context))};
        }

        // if(name === 'techniquesToCompare' && !this.optionManager.isDirectedDrillingUsed(value) && context.numTraditionalTubes === 0) {
        //     newState = {...newState,  ...(this.handleDependencies('numTraditionalTubes', 1, context))};
        // }
        
        // Add/remove for all tubes. Type->Material->Diameter relations
        newState = {...newState,  ...(this.handleTubeDependencies(name, value, 'existingTube', 'numExistingTubes', 3, false, newState, context))};
        newState = {...newState,  ...(this.handleTubeDependencies(name, value, 'traditionalTube', 'numTraditionalTubes', 3, false, newState, context))};
        newState = {...newState,  ...(this.handleTubeDependencies(name, value, 'iflTube', 'numIflTubes', 2, true, newState, context))};
        newState = {...newState,  ...(this.handleTubeDependencies(name, value, 'tcTube', 'numTcTubes', 3, false, newState, context))};
        newState = {...newState,  ...(this.handleTubeDependencies(name, value, 'ddTube', 'numDdTubes', 3, false, newState, context))};
        newState = {...newState,  ...(this.handleTubeDependencies(name, value, 'slTube', 'numSlTubes', 1, false, newState, context))};
        newState = {...newState,  ...(this.handleTubeDependencies(name, value, 'pulTube', 'numPulTubes', 1, false, newState, context))};
        
        newState = this.handleDefaultDiaByTypeEqualOrGreater(name, value, 'iflTube', 2, this.getTubeDiametersIfl.bind(this), newState, context);
        newState = this.handleDefaultDiaByMaterialEqualOrGreater(name, value, 'tcTube', 3, this.getTubeDiametersTc.bind(this), newState, context);
        newState = this.handleDefaultDiaByMaterialSmallest(name, value, 'ddTube', 3, this.getTubeDiametersDd.bind(this), newState, context);
        newState = this.handleDefaultDiaByMaterialSmaller(name, value, 'slTube', 1, this.getTubeDiametersSl.bind(this), newState, context);
        newState = this.handleDefaultDiaByExistingDiaEqualOrGreater(name, value, this.optionManager.ids.TUBE_TYPE_WATER, 'pulTube', this.getTubeDiametersPul.bind(this), newState, context);
        
        // Reset all technique specific data for non used techniques
        newState = this.resetTechniqueSpecificData(name, value, newState, context);

        return newState;
    }
    
    resetTechniqueSpecificData(name: string, value: number, newState: {[name: string]: number}, context: DependencyContext) {
        if(name === 'techniquesToCompare') {
            if(!this.optionManager.isInsertionFlexibleLiningUsed(value) && this.optionManager.isInsertionFlexibleLiningUsed(context.techniquesToCompare)) {
                newState = this.setNamedDependencyDefaultValue(`iflPointShafts`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`iflMetersLongerBottomLengthThanWidth`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`iflShaftDepthM`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`iflShaftCapacityM3PerHour`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`iflShaftLengthM`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`numIflTubes`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`iflTube1TubeType`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`iflTube2TubeType`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`iflTube1TubeDiameter`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`iflTube2TubeDiameter`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`iflTube1TubeLength`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`iflTube2TubeLength`, newState, context);
            }
            if(!this.optionManager.isTubeCrackingUsed(value) && this.optionManager.isTubeCrackingUsed(context.techniquesToCompare)) {
                newState = this.setNamedDependencyDefaultValue(`tcPointShafts`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`tcMetersLongerBottomLengthThanWidth`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`tcShaftDepthM`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`tcShaftCapacityM3PerHour`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`tcShaftLengthM`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`numTcTubes`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`tcTube1TubeType`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`tcTube2TubeType`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`tcTube3TubeType`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`tcTube1TubeMaterial`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`tcTube2TubeMaterial`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`tcTube3TubeMaterial`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`tcTube1TubeDiameter`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`tcTube2TubeDiameter`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`tcTube3TubeDiameter`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`tcTube1TubeLength`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`tcTube2TubeLength`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`tcTube3TubeLength`, newState, context);
            }
            if(!this.optionManager.isDirectedDrillingUsed(value) && this.optionManager.isDirectedDrillingUsed(context.techniquesToCompare)) {
                newState = this.setNamedDependencyDefaultValue(`ddPointShafts`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`ddMetersLongerBottomLengthThanWidth`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`ddShaftDepthM`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`ddShaftCapacityM3PerHour`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`ddShaftLengthM`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`numDdTubes`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`ddTube1TubeType`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`ddTube2TubeType`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`ddTube3TubeType`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`ddTube1TubeMaterial`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`ddTube2TubeMaterial`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`ddTube3TubeMaterial`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`ddTube1TubeDiameter`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`ddTube2TubeDiameter`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`ddTube3TubeDiameter`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`ddTube1TubeLength`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`ddTube2TubeLength`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`ddTube3TubeLength`, newState, context);
            }
            if(!this.optionManager.isSlipliningUsed(value) && this.optionManager.isSlipliningUsed(context.techniquesToCompare)) {
                newState = this.setNamedDependencyDefaultValue(`slPointShafts`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`slMetersLongerBottomLengthThanWidth`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`slShaftDepthM`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`slShaftCapacityM3PerHour`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`slShaftLengthM`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`numSlTubes`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`slTube1TubeType`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`slTube1TubeDiameter`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`slTube1TubeLength`, newState, context);
            }
            if(this.optionManager.isPuLiningUsed(value) && !this.optionManager.isPuLiningUsed(context.techniquesToCompare)) {
                newState = {...newState,  ...(this.handleDependencies('pulTube1TubeType', this.optionManager.ids.TUBE_TYPE_WATER, context))};
            }
            if(this.optionManager.isSlipliningUsed(value) && !this.optionManager.isSlipliningUsed(context.techniquesToCompare)) {
                newState = {...newState,  ...(this.handleDependencies('slTube1TubeType', this.optionManager.ids.TUBE_TYPE_WATER, context))};
            }
            if(value === this.optionManager.ids.TECH_PUL) {
                newState = {...newState, ...(this.handleDependencies('existingTube1TubeType', this.optionManager.ids.TUBE_TYPE_WATER, context))};
                newState = {...newState, ...(this.handleDependencies('numExistingTubes', 1, context))};
                newState = {...newState, ...(this.handleDependencies('numTraditionalTubes', 1, context))};
            }
            if(!this.optionManager.isPuLiningUsed(value) && this.optionManager.isPuLiningUsed(context.techniquesToCompare)) {
                newState = this.setNamedDependencyDefaultValue(`pulPointShafts`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`pulMetersLongerBottomLengthThanWidth`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`pulShaftDepthM`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`pulShaftCapacityM3PerHour`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`pulShaftLengthM`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`numPulTubes`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`pulTube1TubeType`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`pulTube1TubeDiameter`, newState, context);
                newState = this.setNamedDependencyDefaultValue(`pulTube1TubeLength`, newState, context);
            }
        }

        return newState;
    }
    
    handleDefaultDiaByTypeEqualOrGreater(name: string, value: number, prefix: string, numTubes: number, diaCallback: (number, number) => OptionList, newState: {[name: string]: number}, context: DependencyContext) {
        for(let i = 1; i <= numTubes; i++) {
            if (name === `${prefix}${i}TubeType` && value !== -1) {
                let tubeTypeId = value;
                let tubeMaterialId = -1;
                let minDiaId = context.diaByTubeType[tubeTypeId] || -1;
                let diameters = diaCallback(tubeTypeId, tubeMaterialId);
                let diaObj = diameters.find(item => item.id >= minDiaId);
                if (diaObj)
                    newState = {...newState, ...(this.handleDependencies(`${prefix}${i}TubeDiameter`, diaObj.id, context))};
            }
        }
        return newState;
    }

    handleDefaultDiaByExistingDiaEqualOrGreater(name: string, value: number, tubeTypeId: number, noDigPrefix: string, diaCallback: (number, number) => OptionList, newState: {[name: string]: number}, context: DependencyContext) {
        for(let existingTubeIdx = 1; existingTubeIdx <= 3; existingTubeIdx++) {
            if (name === `existingTube${existingTubeIdx}TubeDiameter` && value !== -1 && context[`existingTube${existingTubeIdx}TubeType`] === tubeTypeId) {
                let tubeMaterialId = -1;
                let minDiaId = value;
                let diameters = diaCallback(tubeTypeId, tubeMaterialId);
                let diaObj = diameters.find(item => item.id >= minDiaId);
                if (diaObj)
                    newState = {...newState, ...(this.handleDependencies(`${noDigPrefix}1TubeDiameter`, diaObj.id, context))};
                break;
            }
        }
        return newState;
    }

    handleDefaultDiaByTypeSmaller(name: string, value: number, prefix: string, numTubes: number, diaCallback: (number, number) => OptionList, newState: {[name: string]: number}, context: DependencyContext) {
        for(let i = 1; i <= numTubes; i++) {
            if (name === `${prefix}${i}TubeType` && value !== -1) {
                let tubeTypeId = value;
                let tubeMaterialId = -1;
                let maxDiaId = context.diaByTubeType[tubeTypeId] || -1;
                let diameters = diaCallback(tubeTypeId, tubeMaterialId);
                let diametersReversed = diameters.slice(0).reverse();
                let diaObj = diametersReversed.find(item => item.id < maxDiaId);
                if (diaObj)
                    newState = {...newState, ...(this.handleDependencies(`${prefix}${i}TubeDiameter`, diaObj.id, context))};
            }
        }
        return newState;
    }

    handleDefaultDiaByMaterialEqualOrGreater(name: string, value: number, prefix: string, numTubes: number, diaCallback: (number, number) => OptionList, newState: {[name: string]: number}, context: DependencyContext) {
        for(let i = 1; i <= numTubes; i++) {
            if (name === `${prefix}${i}TubeMaterial` && value !== -1) {
                let tubeTypeId = context[`${prefix}${i}TubeType`];
                let tubeMaterialId = value;
                let minDiaId = context.diaByTubeType[tubeTypeId] || -1;
                let diameters = diaCallback(tubeTypeId, tubeMaterialId);
                let diaObj = diameters.find(item => item.id >= minDiaId);
                if (diaObj)
                    newState = {...newState, ...(this.handleDependencies(`${prefix}${i}TubeDiameter`, diaObj.id, context))};
            }
        }
        return newState;
    }

    handleDefaultDiaByMaterialSmaller(name: string, value: number, prefix: string, numTubes: number, diaCallback: (number, number) => OptionList, newState: {[name: string]: number}, context: DependencyContext) {
        for(let i = 1; i <= numTubes; i++) {
            if (name === `${prefix}${i}TubeMaterial` && value !== -1) {
                let tubeTypeId = context[`${prefix}${i}TubeType`];
                console.log("tube type: ", tubeTypeId);
                let tubeMaterialId = value;
                let maxDiaId = context.diaByTubeType[tubeTypeId] || -1;
                console.log("max: ", maxDiaId);
                let diameters = diaCallback(tubeTypeId, tubeMaterialId);
                let diametersReversed = diameters.slice(0).reverse();
                let diaObj = diametersReversed.find(item => item.id < maxDiaId);
                if (diaObj)
                    newState = {...newState, ...(this.handleDependencies(`${prefix}${i}TubeDiameter`, diaObj.id, context))};
            }
        }
        return newState;
    }

    handleDefaultDiaByMaterialSmallest(name: string, value: number, prefix: string, numTubes: number, diaCallback: (number, number) => OptionList, newState: {[name: string]: number}, context: DependencyContext) {
        for(let i = 1; i <= numTubes; i++) {
            if (name === `${prefix}${i}TubeMaterial` && value !== -1) {
                let tubeTypeId = context[`${prefix}${i}TubeType`];
                let tubeMaterialId = value;
                let diameters = diaCallback(tubeTypeId, tubeMaterialId);
                let diaObj = diameters[0];
                if (diaObj)
                    newState = {...newState, ...(this.handleDependencies(`${prefix}${i}TubeDiameter`, diaObj.id, context))};
            }
        }
        return newState;
    }

    handlePointShaftDependencies(name: string, value: number, namePrefix: string, newState: {[name: string]: number},
                                 context: DependencyContext): {[name: string]: number} {
        if (name === `${namePrefix}PointShafts` && value === 0) {
            newState = this.setNamedDependencyDefaultValue(`${namePrefix}MetersLongerBottomLengthThanWidth`, newState, context);
            newState = this.setNamedDependencyDefaultValue(`${namePrefix}ShaftDepthM`, newState, context);
            newState = this.setNamedDependencyDefaultValue(`${namePrefix}SlopeAngleN1`, newState, context);
            newState = this.setNamedDependencyDefaultValue(`${namePrefix}ShaftCapacityM3PerHour`, newState, context);
            // newState = this.setNamedDependencyDefaultValue(`${namePrefix}ShaftLengthM`, newState);
        }
        return newState;
    }
    
    setNamedDependencyDefaultValue(name: string, newState: {[name: string]: number}, context: DependencyContext) {
        return {...newState, ...(this.handleDependencies(name, this.defaultValueManager.getDefaultValue(name), context))};

    }

    setTubeLengths(prefix: string, numTubes: number, value: number, newState: {[name: string]: number}, context: DependencyContext) {
        for(let i = 1; i <= numTubes; i++) {
            newState = {...newState, ...(this.handleDependencies(`${prefix}${i}TubeLength`, value, context))};
        }
        return newState;
    }
    
    handleTubeDependencies(name: string, value: number, namePrefix: string, numTubesName: string, numTubes: number
        , skipMaterial: boolean, newState: {[name: string]: number}, context: DependencyContext): {[name: string]: number}
    {

        for(let i = 1; i <= numTubes; i++) {
            // Reset values for removed tubes
            if (name === numTubesName && value < i) {
                newState = this.setNamedDependencyDefaultValue(`${namePrefix}${i}TubeType`, newState, context); 
                if(!skipMaterial)
                    newState = this.setNamedDependencyDefaultValue(`${namePrefix}${i}TubeMaterial`, newState, context);  
                newState = this.setNamedDependencyDefaultValue(`${namePrefix}${i}TubeDiameter`, newState, context);  
                // newState = this.setNamedDependencyDefaultValue(`${namePrefix}${i}TubeLength`, newState);  
            }

            // Reset tube material when tube type is changed
            if (!skipMaterial && name === `${namePrefix}${i}TubeType`)
                newState = this.setNamedDependencyDefaultValue(`${namePrefix}${i}TubeMaterial`, newState, context); 
            
            // Reset tube diameter when tube material is changed
            if (name === `${namePrefix}${i}TubeMaterial`)
                newState =this.setNamedDependencyDefaultValue(`${namePrefix}${i}TubeDiameter`, newState, context);
            
        }

        return newState;
    }

    getCoatings(typeOfLandscape: number) : OptionList {
        switch(typeOfLandscape) {
            case this.optionManager.ids.TYPE_OF_LANDSCAPE_STREET:
                return this.optionManager.availableSelections.coatingStreet;

            case this.optionManager.ids.TYPE_OF_LANDSCAPE_VIRGIN:
                return this.optionManager.availableSelections.coatingVirgin;

            default:
                return [];
        }

    }

    getTubeMaterials(tubeType: number): OptionList {
        switch(tubeType) {
            case this.optionManager.ids.TUBE_TYPE_WATER:
                return this.optionManager.availableSelections.tubeMaterialWater;

            case this.optionManager.ids.TUBE_TYPE_SURFACE_WATER:
            case this.optionManager.ids.TUBE_TYPE_DRAIN:
                return this.optionManager.availableSelections.tubeMaterialDrain;

            default:
                return [];

        }
    }

    getTubeMaterialsTrad(tubeType: number): OptionList {
        switch(tubeType) {
            case this.optionManager.ids.TUBE_TYPE_WATER:
                return this.optionManager.availableSelections.tubeMaterialWater;

            case this.optionManager.ids.TUBE_TYPE_SURFACE_WATER:
            case this.optionManager.ids.TUBE_TYPE_DRAIN:
                return this.optionManager.availableSelections.tubeMaterialTradDrain;

            default:
                return [];

        }
    }

    getTubeMaterialsTc(tubeType: number): OptionList {
        switch(tubeType) {
            case this.optionManager.ids.TUBE_TYPE_WATER:
                return this.optionManager.availableSelections.tubeMaterialTcWater;

            case this.optionManager.ids.TUBE_TYPE_SURFACE_WATER:
            case this.optionManager.ids.TUBE_TYPE_DRAIN:
                return this.optionManager.availableSelections.tubeMaterialTcDrain;

            default:
                return [];

        }
    }

    getTubeMaterialsSl(tubeType: number): OptionList {
        return this.optionManager.availableSelections.tubeMaterialSl;
    }

    getTubeMaterialsDd(tubeType: number): OptionList {
        switch(tubeType) {
            case this.optionManager.ids.TUBE_TYPE_WATER:
                return this.optionManager.availableSelections.tubeMaterialDdWater;

            case this.optionManager.ids.TUBE_TYPE_SURFACE_WATER:
            case this.optionManager.ids.TUBE_TYPE_DRAIN:
                return this.optionManager.availableSelections.tubeMaterialDdDrain;

            default:
                return [];

        }
    }

    getTubeDiameters(tubeType: number, tubeMaterial: number): OptionList {
        switch(tubeType) {
            case this.optionManager.ids.TUBE_TYPE_WATER:
                switch(tubeMaterial) {
                    case this.optionManager.ids.TUBE_MATERIAL_PE:
                        return this.optionManager.availableSelections.tubeDiameterWaterPe;
                    case this.optionManager.ids.TUBE_MATERIAL_GREY_IRON:
                        return this.optionManager.availableSelections.tubeDiameterWaterGreyIron;
                    case this.optionManager.ids.TUBE_MATERIAL_STEEL:
                        return this.optionManager.availableSelections.tubeDiameterWaterSteel;
                    default:
                        return [];
                }

            case this.optionManager.ids.TUBE_TYPE_SURFACE_WATER:
            case this.optionManager.ids.TUBE_TYPE_DRAIN:
                switch(tubeMaterial) {
                    case this.optionManager.ids.TUBE_MATERIAL_CONCRETE:
                        return this.optionManager.availableSelections.tubeDiameterDrainConcrete;
                    case this.optionManager.ids.TUBE_MATERIAL_PVC:
                        return this.optionManager.availableSelections.tubeDiameterDrainPvc;
                    case this.optionManager.ids.TUBE_MATERIAL_PE:
                        return this.optionManager.availableSelections.tubeDiameterDrainPe;
                    case this.optionManager.ids.TUBE_MATERIAL_PP:
                        return this.optionManager.availableSelections.tubeDiameterDrainPp;
                    default:
                        return [];
                }
            default:
                return [];
        }
    }

    getTubeDiametersTrad(tubeType: number, tubeMaterial: number): OptionList {
        switch(tubeType) {
            case this.optionManager.ids.TUBE_TYPE_WATER:
                switch(tubeMaterial) {
                    case this.optionManager.ids.TUBE_MATERIAL_PE:
                        return this.optionManager.availableSelections.tubeDiameterTradWaterPe;
                    case this.optionManager.ids.TUBE_MATERIAL_GREY_IRON:
                        return this.optionManager.availableSelections.tubeDiameterWaterGreyIron;
                    case this.optionManager.ids.TUBE_MATERIAL_STEEL:
                        return this.optionManager.availableSelections.tubeDiameterWaterSteel;
                    default:
                        return [];
                }

            case this.optionManager.ids.TUBE_TYPE_SURFACE_WATER:
            case this.optionManager.ids.TUBE_TYPE_DRAIN:
                switch(tubeMaterial) {
                    case this.optionManager.ids.TUBE_MATERIAL_CONCRETE:
                        return this.optionManager.availableSelections.tubeDiameterTradDrainConcrete;
                    case this.optionManager.ids.TUBE_MATERIAL_PVC:
                        return this.optionManager.availableSelections.tubeDiameterDrainPvc;
                    case this.optionManager.ids.TUBE_MATERIAL_PE:
                        return this.optionManager.availableSelections.tubeDiameterDrainPe;
                    case this.optionManager.ids.TUBE_MATERIAL_PP:
                        return this.optionManager.availableSelections.tubeDiameterDrainPp;
                    default:
                        return [];
                }
            default:
                return [];
        }
    }

    getTubeDiametersIfl(tubeType: number, tubeMaterial: number): OptionList {
        return this.optionManager.availableSelections.tubeDiameterIfl;
    }

    getTubeDiametersSl(tubeType: number, tubeMaterial: number): OptionList {
        switch(tubeMaterial) {
            case this.optionManager.ids.TUBE_MATERIAL_PE:
                return this.optionManager.availableSelections.tubeDiameterSlPe;
            case this.optionManager.ids.TUBE_MATERIAL_GREY_IRON:
                return this.optionManager.availableSelections.tubeDiameterSlGreyIron;
            default:
                return [];
        }
    }

    getTubeDiametersPul(tubeType: number, tubeMaterial: number): OptionList {
        return this.optionManager.availableSelections.tubeDiameterPul;
    }

    getTubeDiametersTc(tubeType: number, tubeMaterial: number): OptionList {
        switch(tubeType) {
            case this.optionManager.ids.TUBE_TYPE_WATER:
                switch(tubeMaterial) {
                    case this.optionManager.ids.TUBE_MATERIAL_PE:
                        return this.optionManager.availableSelections.tubeDiameterTcWaterPe;
                    case this.optionManager.ids.TUBE_MATERIAL_GREY_IRON:
                        return this.optionManager.availableSelections.tubeDiameterTcWaterGreyIron;
                    default:
                        return [];
                }

            case this.optionManager.ids.TUBE_TYPE_SURFACE_WATER:
            case this.optionManager.ids.TUBE_TYPE_DRAIN:
                switch(tubeMaterial) {
                    case this.optionManager.ids.TUBE_MATERIAL_PE:
                        return this.optionManager.availableSelections.tubeDiameterTcDrainPe;
                    default:
                        return [];
                }
            default:
                return [];
        }
    }

    getTubeDiametersDd(tubeType: number, tubeMaterial: number): OptionList {
        switch(tubeType) {
            case this.optionManager.ids.TUBE_TYPE_WATER:
                switch(tubeMaterial) {
                    case this.optionManager.ids.TUBE_MATERIAL_PE:
                        return this.optionManager.availableSelections.tubeDiameterDdWaterPe;
                    case this.optionManager.ids.TUBE_MATERIAL_STEEL:
                        return this.optionManager.availableSelections.tubeDiameterWaterSteel;
                    default:
                        return [];
                }

            case this.optionManager.ids.TUBE_TYPE_SURFACE_WATER:
            case this.optionManager.ids.TUBE_TYPE_DRAIN:
                switch(tubeMaterial) {
                    case this.optionManager.ids.TUBE_MATERIAL_PE:
                        return this.optionManager.availableSelections.tubeDiameterDdDrainPe;
                    default:
                        return [];
                }
            default:
                return [];
        }
    }

    isTechnologyValid(options: OptionList, partOfCode: string, value: number) {
        return options
            .filter(option => typeof option.code !== 'undefined' && option.code.includes(partOfCode))
            .map(option => option.id).includes(value);

    }



}


// WEBPACK FOOTER //
// ./src/common/DependencyManager.js